<template>
  <v-autocomplete
    v-model="internalSelection"
    :search-input.sync="search"
    :items="suggestions"
    :loading="loading"
    :hide-no-data="!search"
    :hide-selected="true"
    :item-text="labelFor"
    :item-value="itemValueFn"
    :value-comparator="valueComparator"
    :cache-items="true"
    :return-object="returnObject"
    :label="label"
    :rules="rules"
    :required="required"
    :clearable="true"
    v-bind="$attrs"
    @input="onInputChange"
  >
    <template v-slot:item="{ item }">
      <div class="d-flex flex-column">
        <div class="primary">{{ mainLine(item) }}</div>
        <div class="meta">
          <span>{{ metaLine(item) }}</span>
          <span class="coords" v-if="coordText(item)">· {{ coordText(item) }}</span>
          <span class="provider">· MAPBOX</span>
        </div>
      </div>
    </template>
  </v-autocomplete>
</template>

<script>
const DEFAULT_TOKEN = 'pk.eyJ1IjoibWVkaWNhcmUxMjMiLCJhIjoiY21nNHd5aHd5MDV3YTJrczRldDAzb2FrNCJ9.hv4_cvftMn5j2ENwzROWKg';

export default {
  name: 'ZAAddressAutocomplete',
  inheritAttrs: false,
  props: {
    value: { type: [String, Object], default: '' },
    lat: { type: Number, default: undefined },
    lon: { type: Number, default: undefined },
    label: { type: String, default: 'Search address' },
    rules: { type: Array, default: () => [] },
    required: { type: Boolean, default: false },
    accessToken: { type: String, default: () => (typeof process !== 'undefined' ? (process.env && process.env.VUE_APP_MAPBOX_TOKEN) : '') || DEFAULT_TOKEN },
    country: { type: String, default: 'za' },
    limit: { type: Number, default: 8 },
    bbox: { type: Array, default: () => [16.0, -35.0, 33.5, -22.0] },
    returnObject: { type: Boolean, default: false },
  },
  data() {
    return {
      search: '',
      suggestions: [],
      loading: false,
      error: '',
      _debounceTimer: null,
      _controller: null,
      internalSelection: null,
      proximity: null,
    };
  },
  created() {
    if (typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => {
          const lat = pos.coords.latitude;
          const lon = pos.coords.longitude;
          this.proximity = [lon, lat];
        },
        () => {},
        { enableHighAccuracy: false, maximumAge: 60000, timeout: 3000 }
      );
    }
    // seed from initial value
    if (typeof this.value === 'string') {
      this.search = this.value || '';
      this.internalSelection = this.value || '';
    } else if (this.value && typeof this.value === 'object') {
      this.search = this.labelFor(this.value);
      this.internalSelection = this.returnObject ? this.value : this.search;
      if (this.returnObject) this.ensureSelectionPresent(this.internalSelection);
      // propagate coordinates to parent from prefilled value
      this.syncLatLonFromValue(this.value);
    }
  },
  methods: {
    extractCoordinates(obj) {
      if (!obj) return { lat: undefined, lon: undefined };
      const lat = obj.lat != null ? obj.lat : (obj.latitude != null ? obj.latitude : (obj.coordinates && obj.coordinates.latitude));
      const lonRaw = obj.lon != null ? obj.lon : (obj.lng != null ? obj.lng : (obj.longitude != null ? obj.longitude : (obj.coordinates && obj.coordinates.longitude)));
      const latNum = Number(lat);
      const lonNum = Number(lonRaw);
      return { lat: isNaN(latNum) ? undefined : latNum, lon: isNaN(lonNum) ? undefined : lonNum };
    },
    coordText(item) {
      const { lat, lon } = this.extractCoordinates(item);
      if (typeof lat === 'number' && typeof lon === 'number') {
        return `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
      }
      return '';
    },
    syncLatLonFromValue(val) {
      const { lat, lon } = this.extractCoordinates(val || {});
      this.$emit('update:lat', lat);
      this.$emit('update:lon', lon);
    },
    itemValueFn(item) {
      return this.returnObject ? item : this.labelFor(item);
    },
    ensureSelectionPresent(sel) {
      if (!sel || typeof sel !== 'object') return;
      const exists = this.suggestions.some(it => this.valueComparator(it, sel));
      if (!exists) this.suggestions = [sel, ...this.suggestions];
    },

    onInputChange(val) {
      // user selected or cleared
      if (!val) {
        this.$emit('input', '');
        this.$emit('update:lat', undefined);
        this.$emit('update:lon', undefined);
        return;
      }
      if (typeof val === 'string') {
        // free text (no selection yet)
        this.$emit('input', val);
        return;
      }
      // selected an item object
      const addressText = this.labelFor(val);
      const { lat, lon } = this.extractCoordinates(val);
      this.$emit('selected', { ...val, addressText });
      this.$emit('update:lat', lat);
      this.$emit('update:lon', lon);
      if (this.returnObject) {
        this.$emit('input', val);
      } else {
        this.$emit('input', addressText);
      }
    },

    buildMapboxURL(q) {
      const encodedQ = encodeURIComponent(q);
      const base = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedQ}.json`;
      const url = new URL(base);
      url.searchParams.set('access_token', this.accessToken);
      url.searchParams.set('types', 'address');
      url.searchParams.set('country', this.country);
      url.searchParams.set('limit', String(this.limit));
      url.searchParams.set('language', 'en');
      url.searchParams.set('autocomplete', 'true');
      url.searchParams.set('fuzzyMatch', 'true');
      const [minLon, minLat, maxLon, maxLat] = this.bbox;
      url.searchParams.set('bbox', `${minLon},${minLat},${maxLon},${maxLat}`);
      if (Array.isArray(this.proximity) && this.proximity.length === 2) {
        url.searchParams.set('proximity', `${this.proximity[0]},${this.proximity[1]}`);
      }
      return url;
    },

    async fetchSuggestions(q) {
      try {
        if (!this.accessToken) {
          this.error = 'Missing Mapbox token';
          this.suggestions = [];
          return;
        }

        if (this._controller) this._controller.abort();
        this._controller = new AbortController();

        const url = this.buildMapboxURL(q);
        const res = await fetch(url.toString(), {
          signal: this._controller.signal,
          headers: { Accept: 'application/json' },
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const feats = Array.isArray(data && data.features) ? data.features : [];
        const mapped = feats
          .filter(f => (f.place_type || []).includes('address'))
          .map((f, idx) => this.mapFeature(f, idx));

        this.suggestions = mapped;
        // Ensure current selection remains present after refresh
        if (this.returnObject && this.internalSelection) {
          this.ensureSelectionPresent(this.internalSelection);
        }
      } catch (e) {
        if (e && e.name === 'AbortError') return;
        this.error = 'Could not fetch suggestions. Please try again.';
        this.suggestions = [];
      }
    },

    mapFeature(f, idx) {
      const house = f.address || (f.properties && f.properties.address) || '';
      const road = f.text || (f.properties && (f.properties.street || f.properties.name)) || '';

      const pickCtx = (prefix) => {
        const ctx = Array.isArray(f.context) ? f.context : [];
        const hit = ctx.find(x => x && typeof x.id === 'string' && x.id.indexOf(prefix) === 0);
        return (hit && (hit.text || hit.text_en)) || '';
      };

      const suburb = pickCtx('neighborhood') || pickCtx('district') || pickCtx('locality');
      const city = pickCtx('place');
      const region = pickCtx('region');
      const postcode = pickCtx('postcode');
      const country = pickCtx('country');

      const center = Array.isArray(f.center) ? f.center : [0, 0];
      const lon = center[0];
      const lat = center[1];

      const main = [house, road].filter(Boolean).join(' ');
      const area = [suburb, city, region].filter(Boolean).join(', ');
      const display = (main || area)
        ? [main, area, postcode].filter(Boolean).join(', ')
        : (f.place_name || '');

      return {
        key: `mbx-${f.id || idx}`,
        title: f.text || f.place_name || '',
        house: String(house || ''),
        road: String(road || ''),
        suburb: String(suburb || ''),
        city: String(city || ''),
        region: String(region || ''),
        postcode: String(postcode || ''),
        country: String(country || ''),
        lat: Number(lat),
        lon: Number(lon),
        display,
        _raw: f
      };
    },

    labelFor(s) {
      if (!s) return '';
      if (typeof s === 'string') return s;
      // Prefer structured components when present
      if (s.house || s.road) {
        const locality = [s.suburb, s.city].filter(Boolean).join(', ');
        return [[s.house, s.road].filter(Boolean).join(' '), locality]
          .filter(Boolean)
          .join(', ');
      }
      // Common persisted keys from storage/Firestore
      const persisted = s.display
        || s.title
        || s.addressText
        || s.placeName
        || s.address
        || s.formatted_address
        || s.place_name
        || s.label
        || '';
      if (persisted) return String(persisted);
      // Fallback to typical address line shape
      const line1 = [s.line1, s.address1, s.address_line1].find(Boolean);
      const line2 = [s.line2, s.address2, s.address_line2].find(Boolean);
      const locality = [s.suburb, s.city, s.town, s.locality].find(Boolean);
      const province = s.region || s.province || s.state;
      const postal = s.postcode || s.postalCode || s.zip;
      const parts = [
        [line1, line2].filter(Boolean).join(' '),
        [locality, province].filter(Boolean).join(', '),
        postal,
      ].filter(Boolean);
      return parts.join(', ');
    },
    valueComparator(a, b) {
      if (a === b) return true;
      if (!a || !b) return false;
      if (typeof a === 'string' || typeof b === 'string') return String(a) === String(b);
      // Compare by stable keys if present
      if (a.key && b.key && a.key === b.key) return true;
      const aText = this.labelFor(a);
      const bText = this.labelFor(b);
      if (aText && bText && aText === bText) return true;
      const aC = this.extractCoordinates(a);
      const bC = this.extractCoordinates(b);
      if (typeof aC.lat === 'number' && typeof aC.lon === 'number' && typeof bC.lat === 'number' && typeof bC.lon === 'number') {
        const near = Math.abs(aC.lat - bC.lat) + Math.abs(aC.lon - bC.lon);
        if (near < 1e-10) return true;
      }
      return false;
    },
    mainLine(s) {
      return s && (s.house || s.road) ? [s.house, s.road].filter(Boolean).join(' ') : (s && s.title) || '';
    },
    metaLine(s) {
      if (!s) return '';
      const parts = [s.suburb, s.city, s.region].filter(Boolean).join(', ');
      return s.postcode ? `${parts}, ${s.postcode}` : parts;
    },
  },
  watch: {
    search(val) {
      this.error = '';
      clearTimeout(this._debounceTimer);
      this._debounceTimer = setTimeout(() => {
        const q = (val || '').trim();
        if (!q) {
          this.suggestions = [];
          return;
        }
        this.loading = true;
        this.fetchSuggestions(q).finally(() => (this.loading = false));
      }, 250);
    },
    value(newVal) {
      if (typeof newVal === 'string') {
        this.search = newVal || '';
        this.internalSelection = newVal || '';
      } else if (newVal && typeof newVal === 'object') {
        this.search = this.labelFor(newVal);
        this.internalSelection = this.returnObject ? newVal : this.search;
        if (this.returnObject) this.ensureSelectionPresent(this.internalSelection);
        // propagate coordinates to parent from updated value
        this.syncLatLonFromValue(newVal);
      } else {
        this.internalSelection = '';
        this.search = '';
      }
    },
  },
  beforeDestroy() {
    clearTimeout(this._debounceTimer);
    if (this._controller) this._controller.abort();
  },
};
</script>

<style scoped>
.primary { font-size: 14px; color: #0f172a; font-weight: 600; }
.meta { font-size: 12px; color: #64748b; }
.coords, .provider { margin-left: 8px; font-variant-numeric: tabular-nums; }
</style>


